import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import Select, { MultiValue } from 'react-select';
import { useParams } from "react-router-dom";
import { fetchBlogBySlug } from "../../api-client";
import { BlogType } from "../../../../backend/src/shared/types";

interface ListItem {
  header: string;
  paragraph: string;
}

interface Section {
  heading: string;
  text: string;
  image: File | string | null;
  listItems: ListItem[];
}

interface ManageBlogFormProps {
  onSave: (formData: FormData, slug?: string) => void;
  isLoading: boolean;
  isSaving: boolean; // New prop to indicate saving state
  setSaving: (isSaving: boolean) => void; // New prop to set saving state
}

const ManageBlogForm: React.FC<ManageBlogFormProps> = ({ onSave, isSaving, setSaving }) => {
  const { slug } = useParams<{ slug: string }>();

  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [metaTitle, setMetaTitle] = useState<string>("");
  const [metaDescription, setMetaDescription] = useState<string>("");
  const [quote, setQuote] = useState<string[]>([]);
  const [mainImage, setMainImage] = useState<File | null>(null);
  const [mainImageUrl, setMainImageUrl] = useState<string>("");
  const [subImages, setSubImages] = useState<File[]>([]);
  const [subImageUrls, setSubImageUrls] = useState<string[]>([]);
  const [sections, setSections] = useState<Section[]>([
    { heading: "", text: "", image: null, listItems: [{ header: "", paragraph: "" }] },
  ]);
  const [selectedTags, setSelectedTags] = useState<{ label: string; value: string; }[]>([]);

  useEffect(() => {
    const loadBlogData = async () => {
      if (slug) {
        try {
          const blog: BlogType = await fetchBlogBySlug(slug);
          setTitle(blog.title);
          setContent(blog.content);
          setType(blog.type);
          setMetaTitle(blog.metaTitle);
          setMetaDescription(blog.metaDescription);
          setQuote(blog.quote);
          if (blog.imageUrls.length > 0) {
            setMainImageUrl(blog.imageUrls[0]);
            setSubImageUrls(blog.imageUrls.slice(1));
          }
          setSections(blog.sections.map(section => ({
            heading: section.heading,
            text: section.text,
            image: section.image || null,
            listItems: section.listItems
          }) as Section));
          setSelectedTags(blog.tags.map(tag => ({ label: tag, value: tag })));
        } catch (error) {
          console.error("Error loading blog data: ", error);
        }
      }
    };

    loadBlogData();
  }, [slug]);

  const handleQuoteChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuote(e.target.value.split(","));
  };

  const handleMainImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setMainImage(e.target.files[0]);
    }
  };

  const handleSubImagesChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSubImages(Array.from(e.target.files));
    }
  };

  const handleSectionChange = (index: number, field: string, value: string | File | null) => {
    const newSections = sections.slice();
    newSections[index] = { ...newSections[index], [field]: value };
    setSections(newSections);
  };

  const handleListItemChange = (sectionIndex: number, listItemIndex: number, field: string, value: string) => {
    const newSections = sections.slice();
    const newListItems = newSections[sectionIndex].listItems.slice();
    newListItems[listItemIndex] = { ...newListItems[listItemIndex], [field]: value };
    newSections[sectionIndex].listItems = newListItems;
    setSections(newSections);
  };

  const handleTagsChange = (newValue: MultiValue<{ label: string; value: string }>) => {
    setSelectedTags(newValue as { label: string; value: string }[]);
  };

  const addSection = () => {
    setSections([...sections, { heading: "", text: "", image: null, listItems: [{ header: "", paragraph: "" }] }]);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const addListItem = (sectionIndex: number) => {
    const newSections = sections.slice();
    newSections[sectionIndex].listItems.push({ header: "", paragraph: "" });
    setSections(newSections);
  };

  const removeListItem = (sectionIndex: number, listItemIndex: number) => {
    const newSections = sections.slice();
    newSections[sectionIndex].listItems = newSections[sectionIndex].listItems.filter((_, i) => i !== listItemIndex);
    setSections(newSections);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true); // Set saving state to true
    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("type", type);
    formData.append("metaTitle", metaTitle);
    formData.append("metaDescription", metaDescription);
    formData.append("quote", JSON.stringify(quote));
    formData.append("tags", JSON.stringify(selectedTags.map(tag => tag.value)));

    // Append images if they exist, otherwise append existing URLs
    if (mainImage) {
      formData.append("mainImage", mainImage);
    } else if (mainImageUrl) {
      formData.append("mainImageUrl", mainImageUrl);
    }

    if (subImages.length > 0) {
      subImages.forEach((file) => formData.append("subImages", file));
    } else if (subImageUrls.length > 0) {
      formData.append("subImageUrls", JSON.stringify(subImageUrls));
    }

    const sectionImages: Array<{ file: File | null; index: number }> = [];

    formData.append("sections", JSON.stringify(sections.map((section, index) => {
      if (section.image && typeof section.image !== 'string') {
        sectionImages.push({ file: section.image, index });
        return { ...section, image: null }; // Placeholder to be replaced with actual file name
      }
      return section;
    })));

    sectionImages.forEach(({ file, index }) => {
      if (file) formData.append(`sectionImages${index}`, file);
    });

    onSave(formData, slug);
  };

  const tagOptions = [
    { label: 'All', value: 'all' },
    { label: 'Beach Vibes', value: 'beach_vibes' },
    { label: 'Adventure', value: 'adventure' },
    { label: 'Love and Travel', value: 'love_and_travel' },
    { label: 'All Inclusive', value: 'all_inclusive' },
    { label: 'Family Compass', value: 'family_compass' },
  ];

  const getImageSrc = (image: File | string | null): string | undefined => {
    if (typeof image === 'string') return image;
    return image ? URL.createObjectURL(image) : undefined;
  };

  return (
    <div className="p-6  mt-10">
      <h1 className="text-3xl font-bold text-center mb-4">Add New Blog</h1>
      <hr className="border-gray-200 mb-6 w-10/12 mx-auto" />
      <form onSubmit={handleSubmit} className="p-4 space-y-4 mt-10">
        {/* Form content here */}
        <div className="space-y-4">
          {/* Title and Type Fields in One Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <input
                type="text"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Meta Title and Meta Description Fields in One Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Meta Title</label>
              <input
                type="text"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Meta Description</label>
              <input
                type="text"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Tags Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Tags</label>
            <Select
              isMulti
              value={selectedTags}
              onChange={handleTagsChange}
              options={tagOptions}
              className="mt-1"
              classNamePrefix="react-select"
            />
          </div>

          {/* Content Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          {/* Quotes Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Quotes</label>
            <input
              type="text"
              value={quote.join(",")}
              onChange={handleQuoteChange}
              className="mt-1 p-3 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          {/* Main Image */}
          {mainImageUrl && !mainImage && (
            <div>
              <img width={600} src={mainImageUrl} alt="Main" className="mb-4 rounded-md shadow-sm" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Main Image</label>
            <input
              type="file"
              name="mainImage"
              onChange={handleMainImageChange}
              className="mt-1"
            />
          </div>

          {/* Sub Images */}
          {subImageUrls.length > 0 && subImages.length === 0 && (
            <div className="space-y-2">
              {subImageUrls.map((url, index) => (
                <img width={300} key={index} src={url} alt={`Sub ${index + 1}`} className="mb-4 rounded-md shadow-sm" />
              ))}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Featured Images</label>
            <input
              type="file"
              name="subImages"
              multiple
              onChange={handleSubImagesChange}
              className="mt-1"
            />
          </div>
        </div>
        {sections.map((section, index) => (
          <div key={index} className="border border-gray-300 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-2">Section {index + 1}</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Heading</label>
              <input
                type="text"
                value={section.heading}
                onChange={(e) => handleSectionChange(index, "heading", e.target.value)}
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Text</label>
              <textarea
                value={section.text}
                onChange={(e) => handleSectionChange(index, "text", e.target.value)}
                className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
              />
            </div>
            {section.image && <img width={400} src={getImageSrc(section.image)} alt="Section" className="my-4" />}
            <div>
              <label className="block text-sm font-medium text-gray-700">Image</label>
              <input
                type="file"
                name={`sectionImages${index}`}
                onChange={(e) => handleSectionChange(index, "image", e.target.files ? e.target.files[0] : null)}
                className="mt-1"
              />
            </div>
            <div className="mt-4">
              <h4 className="text-md font-medium">List Items</h4>
              {section.listItems.map((listItem, listItemIndex) => (
                <div key={listItemIndex} className="mt-2 space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Header</label>
                    <input
                      type="text"
                      value={listItem.header}
                      onChange={(e) => handleListItemChange(index, listItemIndex, "header", e.target.value)}
                      className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paragraph</label>
                    <textarea
                      value={listItem.paragraph}
                      onChange={(e) => handleListItemChange(index, listItemIndex, "paragraph", e.target.value)}
                      className="mt-1 p-2 block w-full border border-gray-300 rounded-md"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => addListItem(index)}
                    className="mt-4 px-4 py-2 border-1 shadow mx-4 text-black rounded-md"
                  >
                    Add Item
                  </button>
                  <button
                    type="button"
                    onClick={() => removeListItem(index, listItemIndex)}
                    className="mt-2 px-4 py-2 text-red-500 rounded-md"
                  >
                    Remove Item
                  </button>
                </div>
              ))}
            </div>
            <div className="justify-center flex">
              <button
                type="button"
                onClick={() => removeSection(index)}
                className="px-4 text-red-500 rounded-md"
              >
                - Remove Section
              </button>
            </div>
          </div>
        ))}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={addSection}
            className="mt-4 px-4 py-2 text-black rounded-md"
          >
            + Add Section
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="mt-4 px-4 py-2 bg-green-200 border-1 border-green-500 text-black rounded-md shadow"
          >
            {isSaving ? "Saving..." : "Save Blog"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageBlogForm;